import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc, collection, getDocs, updateDoc, addDoc, deleteDoc, query, orderBy, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Dialog, Transition } from '@headlessui/react';
import { formatFirestoreDate } from '../utils/dateUtils';
import Avatar from '../components/Avatar';
import FamilyImageUpload from '../components/FamilyImageUpload';
import { deleteFamilyPhoto } from '../utils/familyImageUpload';
import { MicrophoneIcon, PencilSquareIcon, TrophyIcon, HeartIcon, TrashIcon, ChartBarIcon, SpeakerWaveIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import { getLimits, getFamilyPlan, PLAN_LABELS, getUserPlan } from '../utils/planLimits';
import { displayFamilyCode, generateUniqueFamilyCode } from '../utils/familyCode';
import CompletedChapters from './CompletedChapters';

function FamilyPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('activities');
  const [selectedFamilyId, setSelectedFamilyId] = useState(null);

  // State for real data
  const [userFamilies, setUserFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [members, setMembers] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [editingFamilyName, setEditingFamilyName] = useState('');
  const [editingFamilyBio, setEditingFamilyBio] = useState('');
  const [savingFamilyName, setSavingFamilyName] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recordingToDelete, setRecordingToDelete] = useState(null);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [memberToAdmin, setMemberToAdmin] = useState(null);
  const [familyPhotoUrl, setFamilyPhotoUrl] = useState('');
  const [familyPhotoPath, setFamilyPhotoPath] = useState('');
  const [familyListed, setFamilyListed] = useState(true);
  const [togglingListed, setTogglingListed] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [memberToView, setMemberToView] = useState(null);
  const [removeMemberModalOpen, setRemoveMemberModalOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [removedMembers, setRemovedMembers] = useState([]);
  const [removedOpen, setRemovedOpen] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [codeGenerating, setCodeGenerating] = useState(false);
  const [activities, setActivities] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [activitiesPerPage] = useState(10);
  const [invitations, setInvitations] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [noteInteractions, setNoteInteractions] = useState({});
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [commentDraft, setCommentDraft] = useState({});
  const [activityLikes, setActivityLikes] = useState({});
  const [memberPermissions, setMemberPermissions] = useState({});

  // Handle authentication state
  useEffect(() => {
    if (currentUser === null) {
      // User is not authenticated, stop initializing
      setInitializing(false);
    }
  }, [currentUser]);

  // Fetch user's families and data
  useEffect(() => {
    async function fetchUserFamilies() {
      if (!currentUser) {
        return;
      }
      
      setLoading(true);
      try {
        // AuthContext already fetched users/{uid} and merged familyIds onto
        // currentUser — re-fetching it here was a second, unguarded network
        // call with no timeout: if it ever hung on a slow/cold mobile
        // connection, nothing below it (including setLoading(false)) ever
        // ran, leaving the page blank/frozen forever. Read what's already in
        // memory instead.
        const familyIds = currentUser.familyIds || [];

        if (familyIds.length === 0) {
          setLoading(false);
          setInitializing(false);
          return;
        }

        // Fetch all user's families in parallel — was sequential, one round
        // trip at a time.
        const familyDocs = await Promise.all(
          familyIds.map(familyId =>
            getDoc(doc(db, 'families', familyId)).catch(error => {
              console.error(`Error fetching family ${familyId}:`, error);
              return null;
            })
          )
        );
        const familiesData = familyDocs
          .filter(d => d && d.exists())
          .map(d => ({ id: d.id, ...d.data() }));

        setUserFamilies(familiesData);

        // If navigated here from Manage Family with a specific family + tab, pre-select it.
        const navFamilyId = location.state?.familyId;
        const navTab = location.state?.tab;
        const preselect = navFamilyId
          ? (familiesData.find(f => f.id === navFamilyId) || familiesData[0])
          : familiesData[0];

        // Select first family by default (or the one passed via nav state)
        if (preselect) {
          setSelectedFamilyId(preselect.id);
          setSelectedFamily(preselect);
          if (preselect.familyPhotoUrl) {
            setFamilyPhotoUrl(preselect.familyPhotoUrl);
            setFamilyPhotoPath(preselect.familyPhotoPath || '');
          }
          if (navTab) setActiveTab(navTab);
        }
        
      } catch (error) {
        console.error('Error fetching user families:', error);
      } finally {
        setLoading(false);
        setInitializing(false);
      }
    }
    
    fetchUserFamilies();
  }, [currentUser]);

  // Fetch selected family data
  useEffect(() => {
    async function fetchFamilyData() {
      if (!selectedFamilyId) return;

      // Each section below runs independently and catches its own errors —
      // this used to be one long sequential chain in a single try/catch, so
      // a single failing/slow step (often the two full-collection scans this
      // replaces) silently aborted every step after it, including sections
      // with no relation to the failure (e.g. a recordings hiccup used to
      // take down activities too). Promise.allSettled means a member, a
      // recordings scope, or an activities fetch failing never blocks the
      // others from rendering.

      // Members + permissions — needs the family doc for memberIds.
      const membersBranch = (async () => {
        try {
          const familyDoc = await getDoc(doc(db, 'families', selectedFamilyId));
          const familyData = familyDoc.data();

          const membersSnap = await getDocs(collection(db, 'families', selectedFamilyId, 'members'));
          const memberDocMap = {};
          const permMap = {};
          for (const d of membersSnap.docs) {
            const data = d.data();
            memberDocMap[d.id] = data;
            permMap[d.id] = {
              canRecord: data.canRecord !== false,
              canCommentLike: data.canCommentLike !== false,
              canAddNotes: data.canAddNotes !== false,
              canInvite: data.canInvite !== false,
            };
          }
          setMemberPermissions(permMap);

          if (familyData && (familyData.memberIds || familyData.memberIDs)) {
            const memberIds = familyData.memberIds || familyData.memberIDs;
            // Fetch member profiles in parallel — was sequential, one round
            // trip per member.
            const memberDocs = await Promise.all(
              memberIds.map(memberId =>
                getDoc(doc(db, 'users', memberId)).catch(error => {
                  console.error(`Error fetching member ${memberId}:`, error);
                  return null;
                })
              )
            );
            const memberList = [];
            memberDocs.forEach((userDoc, i) => {
              const memberId = memberIds[i];
              if (!userDoc || !userDoc.exists()) return;
              memberList.push({ id: userDoc.id, ...userDoc.data() });
              // Backfill member doc if missing
              if (!memberDocMap[memberId]) {
                const ud = userDoc.data();
                const isCreator = familyData.createdBy === memberId;
                const isAdmin = familyData.adminIDs?.includes(memberId);
                const newDoc = {
                  userId: memberId,
                  userEmail: ud.email || '',
                  displayName: ud.displayName || ud.email || '',
                  photoURL: ud.photoURL || '',
                  bio: ud.bio || '',
                  joinedAt: new Date().toISOString(),
                  role: isCreator ? 'admin' : isAdmin ? 'admin' : 'member',
                  canRecord: true,
                  canCommentLike: true,
                  canAddNotes: true,
                  canInvite: isCreator || isAdmin || true,
                };
                setDoc(doc(db, 'families', selectedFamilyId, 'members', memberId), newDoc).catch(() => {});
                permMap[memberId] = { canRecord: true, canCommentLike: true, canAddNotes: true, canInvite: true };
              }
            });
            setMembers(memberList);
            setMemberPermissions({ ...permMap });
          }
        } catch (error) {
          console.error('Error fetching family members:', error);
        }
      })();

      // Recordings — scoped query instead of scanning every recording in
      // the app and filtering client-side.
      const recordingsBranch = (async () => {
        try {
          const recordingsSnapshot = await getDocs(
            query(collection(db, 'recordings'), where('familyIds', 'array-contains', selectedFamilyId))
          );
          setRecordings(recordingsSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (error) {
          console.error('Error fetching family recordings:', error);
        }
      })();

      // Invitations
      const invitationsBranch = refreshInvitations(selectedFamilyId).catch(error => {
        console.error('Error fetching family invitations:', error);
      });

      // Pending join requests (submitted via family code)
      const joinRequestsBranch = (async () => {
        try {
          const requestsSnap = await getDocs(
            query(collection(db, 'families', selectedFamilyId, 'requests'), where('status', '==', 'pending'))
          );
          setJoinRequests(requestsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (error) {
          console.error('Error fetching join requests:', error);
        }
      })();

      // Removed members — scoped query instead of scanning every user in
      // the app to find who has this family in their removedFamilyIds.
      const removedMembersBranch = (async () => {
        try {
          const removedSnap = await getDocs(
            query(collection(db, 'users'), where('removedFamilyIds', 'array-contains', selectedFamilyId))
          );
          setRemovedMembers(removedSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (error) {
          console.error('Error fetching removed members:', error);
        }
      })();

      // Activities
      const activitiesBranch = fetchFamilyActivities(selectedFamilyId).catch(error => {
        console.error('Error fetching family activities:', error);
      });

      await Promise.allSettled([
        membersBranch,
        recordingsBranch,
        invitationsBranch,
        joinRequestsBranch,
        removedMembersBranch,
        activitiesBranch,
      ]);

      // Reset pagination when family changes — independent of whether any
      // fetch above succeeded.
      setCurrentPage(1);
    }
    
    fetchFamilyData();
  }, [selectedFamilyId]);

  // Load activity likes for the current page
  useEffect(() => {
    if (activities.length === 0 || !currentUser) return;
    const start = (currentPage - 1) * activitiesPerPage;
    const pageIds = activities.slice(start, start + activitiesPerPage).map(a => a.id);
    if (pageIds.length === 0) return;
    getDocs(query(collection(db, 'activityLikes'), where('activityId', 'in', pageIds)))
      .then(snap => {
        const map = {};
        pageIds.forEach(id => { map[id] = { count: 0, likedByMe: false, myLikeId: null }; });
        snap.docs.forEach(d => {
          const data = d.data();
          const id = data.activityId;
          if (!map[id]) map[id] = { count: 0, likedByMe: false, myLikeId: null };
          map[id].count++;
          if (data.likedByUid === currentUser.uid) {
            map[id].likedByMe = true;
            map[id].myLikeId = d.id;
          }
        });
        setActivityLikes(prev => ({ ...prev, ...map }));
      })
      .catch(() => {});
  }, [currentPage, activities]); // eslint-disable-line

  // Update editing family name and photo when selected family changes
  useEffect(() => {
    if (selectedFamily) {
      setEditingFamilyName(selectedFamily.name || '');
      setEditingFamilyBio(selectedFamily.bio || '');
      setFamilyPhotoUrl(selectedFamily.familyPhotoUrl || '');
      setFamilyPhotoPath(selectedFamily.familyPhotoPath || '');
      setFamilyListed(selectedFamily.listed !== false);
    }
  }, [selectedFamily]);

  const handleGenerateFamilyCode = async () => {
    setCodeGenerating(true);
    try {
      const code = await generateUniqueFamilyCode(selectedFamily.name);
      await updateDoc(doc(db, 'families', selectedFamilyId), { familyCode: code });
      setSelectedFamily({ ...selectedFamily, familyCode: code });
    } catch (err) {
      console.error('Failed to generate family code:', err);
    } finally {
      setCodeGenerating(false);
    }
  };

  const handleSaveFamilyName = async () => {
    if (!selectedFamily || !editingFamilyName) return;
    setSavingFamilyName(true);
    try {
      await updateDoc(doc(db, 'families', selectedFamily.id), {
        name: editingFamilyName,
        bio: editingFamilyBio.trim(),
      });
      setSelectedFamily({ ...selectedFamily, name: editingFamilyName, bio: editingFamilyBio.trim() });
      setUserFamilies(prev =>
        prev.map(f =>
          f.id === selectedFamily.id
            ? { ...f, name: editingFamilyName, bio: editingFamilyBio.trim() }
            : f
        )
      );
    } catch (error) {
      console.error('Error updating family info:', error);
    } finally {
      setSavingFamilyName(false);
    }
  };

  const handleToggleListed = async (newValue) => {
    if (!selectedFamily) return;
    setTogglingListed(true);
    try {
      await updateDoc(doc(db, 'families', selectedFamily.id), { listed: newValue });
      setFamilyListed(newValue);
      setSelectedFamily({ ...selectedFamily, listed: newValue });
    } catch (err) {
      console.error('Error updating listed:', err);
    } finally {
      setTogglingListed(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail || !selectedFamily) return;

    // Enforce the member limit here, at send time — the accept side no longer
    // checks, so all seat accounting happens on this button. Friends Access
    // (the `beta` plan / isBeta flag) grants unlimited members: use the most
    // permissive plan between the family and the inviting user.
    const familyPlan = getFamilyPlan(selectedFamily);
    const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
    const userPlan = getUserPlan(userSnap.data());
    const effectivePlan = userPlan === 'beta' ? 'beta' : familyPlan;
    const { memberCount: memberLimit } = getLimits(effectivePlan);

    // A pending invitation reserves a seat too — otherwise several invites could
    // be sent under the limit and then all accept, overshooting it. Count current
    // members plus distinct pending invitees (a resend to an existing pending
    // invitee doesn't consume an additional seat).
    const memberCount = (selectedFamily.memberIDs || selectedFamily.memberIds || []).length;
    const targetEmail = inviteEmail.trim().toLowerCase();
    const pendingEmails = new Set(
      (invitations || [])
        .filter(inv => inv.status === 'pending')
        .map(inv => (inv.invitedUserEmail || '').toLowerCase())
        .filter(Boolean)
    );
    const projectedSeats = memberCount + pendingEmails.size + (pendingEmails.has(targetEmail) ? 0 : 1);
    if (projectedSeats > memberLimit) {
      const planLabel = PLAN_LABELS[effectivePlan] || effectivePlan;
      alert(
        `${selectedFamily.name} is at its ${memberLimit}-member limit for the ${planLabel} ` +
        `(current members plus pending invitations).\n\n` +
        `Upgrade at lineagebible.com to add more members.`
      );
      return;
    }

    setSendingInvite(true);
    try {
      // Create invitation
      await addDoc(collection(db, 'families', selectedFamily.id, 'invitations'), {
        invitedUserEmail: inviteEmail,
        invitedUserId: null, // Will be set when user accepts
        familyId: selectedFamily.id,
        familyName: selectedFamily.name,
        invitedBy: currentUser.uid,
        invitedByName: currentUser.displayName || currentUser.email,
        status: 'pending',
        createdAt: new Date().toISOString()
      });
      
      setInviteEmail('');
      
      // Refresh invitations list
      await refreshInvitations();
      
      alert('Invitation sent successfully!');
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert('Failed to send invitation. Please try again.');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleResendInvitation = async (invitation) => {
    if (!selectedFamily) return;
    
    try {
      // Create a new invitation with updated timestamp
      await addDoc(collection(db, 'families', selectedFamily.id, 'invitations'), {
        invitedUserEmail: invitation.invitedUserEmail,
        invitedUserId: null,
        familyId: selectedFamily.id,
        familyName: selectedFamily.name,
        invitedBy: currentUser.uid,
        invitedByName: currentUser.displayName || currentUser.email,
        status: 'pending',
        createdAt: new Date().toISOString()
      });
      
      // Refresh invitations list
      await refreshInvitations();
      
      alert('Invitation resent successfully!');
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert('Failed to resend invitation. Please try again.');
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (!selectedFamily) return;
    
    if (!window.confirm('Are you sure you want to delete this invitation?')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'families', selectedFamily.id, 'invitations', invitationId));
      
      // Refresh invitations list
      await refreshInvitations();
      
      alert('Invitation deleted successfully!');
    } catch (error) {
      console.error('Error deleting invitation:', error);
      alert('Failed to delete invitation. Please try again.');
    }
  };

  const refreshInvitations = async (familyId = null) => {
    const targetFamilyId = familyId || selectedFamily?.id;
    if (!targetFamilyId) return;
    
    const invitationsSnapshot = await getDocs(
      query(
        collection(db, 'families', targetFamilyId, 'invitations'),
        orderBy('createdAt', 'desc')
      )
    );
    const invitationsList = invitationsSnapshot.docs.map(docSnap => ({ 
      id: docSnap.id, 
      ...docSnap.data() 
    }));
    setInvitations(invitationsList);
  };

  const handleApproveRequest = async (request) => {
    try {
      const { doc: fsDoc, updateDoc, setDoc, arrayUnion, serverTimestamp } = await import('firebase/firestore');
      // Add to family memberIDs
      await updateDoc(fsDoc(db, 'families', selectedFamilyId), {
        memberIDs: arrayUnion(request.userId),
      });
      // Create member subdoc
      await setDoc(fsDoc(db, 'families', selectedFamilyId, 'members', request.userId), {
        userId: request.userId,
        userEmail: request.userEmail,
        displayName: request.displayName,
        photoURL: request.photoURL || '',
        bio: '',
        role: 'member',
        canRecord: true,
        canInvite: true,
        canCommentLike: true,
        canAddNotes: true,
        joinedAt: new Date().toISOString(),
      });
      // Add family to user's familyIds and clear pending state
      const { deleteField } = await import('firebase/firestore');
      await updateDoc(fsDoc(db, 'users', request.userId), {
        familyIds: arrayUnion(selectedFamilyId),
        pendingFamilyId: deleteField(),
        pendingFamilyName: deleteField(),
      });
      // Mark request as approved
      await updateDoc(fsDoc(db, 'families', selectedFamilyId, 'requests', request.id), {
        status: 'approved',
        resolvedAt: serverTimestamp(),
      });
      setJoinRequests(prev => prev.filter(r => r.id !== request.id));
      // Refresh member list
      const userDoc = await import('firebase/firestore').then(({ getDoc, doc: d }) => getDoc(d(db, 'users', request.userId)));
      if (userDoc.exists()) {
        setMembers(prev => [...prev, { id: request.userId, ...userDoc.data() }]);
      }
    } catch (err) {
      console.error('Error approving request:', err);
      alert('Failed to approve request. Please try again.');
    }
  };

  const handleDenyRequest = async (request) => {
    try {
      const { doc: fsDoc, updateDoc, serverTimestamp, deleteField } = await import('firebase/firestore');
      await updateDoc(fsDoc(db, 'families', selectedFamilyId, 'requests', request.id), {
        status: 'denied',
        resolvedAt: serverTimestamp(),
      });
      await updateDoc(fsDoc(db, 'users', request.userId), {
        pendingFamilyId: deleteField(),
        pendingFamilyName: deleteField(),
      });
      setJoinRequests(prev => prev.filter(r => r.id !== request.id));
    } catch (err) {
      console.error('Error denying request:', err);
      alert('Failed to deny request. Please try again.');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!selectedFamily || memberId === currentUser.uid) return;
    
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    
    try {
      // Remove from family memberIDs
      const updatedMemberIDs = selectedFamily.memberIDs.filter(id => id !== memberId);
      await updateDoc(doc(db, 'families', selectedFamily.id), {
        memberIDs: updatedMemberIDs
      });
      
      // Remove family from user's familyIds
      const userDoc = await getDoc(doc(db, 'users', memberId));
      const userData = userDoc.data();
      if (userData && userData.familyIds) {
        const updatedFamilyIds = userData.familyIds.filter(id => id !== selectedFamily.id);
        await updateDoc(doc(db, 'users', memberId), {
          familyIds: updatedFamilyIds
        });
      }
      
      // Update local state
      setSelectedFamily({ ...selectedFamily, memberIDs: updatedMemberIDs });
      setMembers(prev => prev.filter(m => m.id !== memberId));
      
      alert('Member removed successfully');
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member. Please try again.');
    }
  };

  const handleDeleteRecording = async (recording) => {
    // Check if user is family creator, admin, or recording owner
    if (selectedFamily.createdBy !== currentUser.uid && 
        !selectedFamily.adminIDs?.includes(currentUser.uid) && 
        recording.userId !== currentUser.uid) {
      alert('Only family admins or recording owners can delete recordings.');
      return;
    }
    
    setRecordingToDelete(recording);
    setDeleteModalOpen(true);
  };

  const confirmDeleteRecording = async () => {
    if (!recordingToDelete) return;
    
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'recordings', recordingToDelete.id));
      
      // Remove from local state
      setRecordings(prev => prev.filter(r => r.id !== recordingToDelete.id));
      
      // Close modal
      setDeleteModalOpen(false);
      setRecordingToDelete(null);
      
      alert('Recording deleted successfully');
    } catch (error) {
      console.error('Error deleting recording:', error);
      alert('Failed to delete recording. Please try again.');
    }
  };

  const handleAddAdmin = async (member) => {
    if (!selectedFamily || member.id === currentUser.uid) return;
    
    setMemberToAdmin(member);
    setAdminModalOpen(true);
  };

  const confirmAddAdmin = async () => {
    if (!memberToAdmin || !selectedFamily) return;
    
    try {
      // Initialize adminIDs array if it doesn't exist
      const currentAdminIDs = selectedFamily.adminIDs || [];
      const updatedAdminIDs = [...currentAdminIDs, memberToAdmin.id];
      
      // Update family document with new admin
      await updateDoc(doc(db, 'families', selectedFamily.id), {
        adminIDs: updatedAdminIDs
      });
      
      // Update local state
      setSelectedFamily({ ...selectedFamily, adminIDs: updatedAdminIDs });
      
      // Close modal
      setAdminModalOpen(false);
      setMemberToAdmin(null);
      
      alert(`${memberToAdmin.displayName || memberToAdmin.email} is now an admin!`);
    } catch (error) {
      console.error('Error adding admin:', error);
      alert('Failed to add admin. Please try again.');
    }
  };

  const handleRemoveAdmin = async (member) => {
    if (!selectedFamily || member.id === currentUser.uid) return;
    
    if (!window.confirm(`Are you sure you want to remove admin privileges from ${member.displayName || member.email}?`)) {
      return;
    }
    
    try {
      const currentAdminIDs = selectedFamily.adminIDs || [];
      const updatedAdminIDs = currentAdminIDs.filter(id => id !== member.id);
      
      // Update family document
      await updateDoc(doc(db, 'families', selectedFamily.id), {
        adminIDs: updatedAdminIDs
      });
      
      // Update local state
      setSelectedFamily({ ...selectedFamily, adminIDs: updatedAdminIDs });
      
      alert(`${member.displayName || member.email} is no longer an admin.`);
    } catch (error) {
      console.error('Error removing admin:', error);
      alert('Failed to remove admin privileges. Please try again.');
    }
  };

  const handleViewProfile = (member) => {
    setMemberToView(member);
    setProfileModalOpen(true);
  };

  const handleRemoveMemberFromDrawer = (member) => {
    setMemberToRemove(member);
    setRemoveMemberModalOpen(true);
  };

  const confirmRemoveMember = async () => {
    if (!memberToRemove || !selectedFamily) return;
    
    try {
      // Remove from family memberIDs (soft delete)
      const updatedMemberIDs = selectedFamily.memberIDs.filter(id => id !== memberToRemove.id);
      await updateDoc(doc(db, 'families', selectedFamily.id), {
        memberIDs: updatedMemberIDs
      });
      
      // Add family to user's removedFamilyIds (soft delete)
      const userDoc = await getDoc(doc(db, 'users', memberToRemove.id));
      const userData = userDoc.data();
      if (userData) {
        const currentFamilyIds = userData.familyIds || [];
        const currentRemovedFamilyIds = userData.removedFamilyIds || [];
        
        // Remove from familyIds and add to removedFamilyIds
        const updatedFamilyIds = currentFamilyIds.filter(id => id !== selectedFamily.id);
        const updatedRemovedFamilyIds = [...currentRemovedFamilyIds, selectedFamily.id];
        
        await updateDoc(doc(db, 'users', memberToRemove.id), {
          familyIds: updatedFamilyIds,
          removedFamilyIds: updatedRemovedFamilyIds
        });
      }
      
      // Update local state
      setSelectedFamily({ ...selectedFamily, memberIDs: updatedMemberIDs });
      setMembers(prev => prev.filter(m => m.id !== memberToRemove.id));
      setRemovedMembers(prev => [...prev, memberToRemove]);
      
      // Close modals
      setRemoveMemberModalOpen(false);
      setProfileModalOpen(false);
      setMemberToRemove(null);
      
      alert(`${memberToRemove.displayName || memberToRemove.email} has been removed from the family. Their recordings and data are preserved.`);
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member. Please try again.');
    }
  };

  const handleRestoreMember = async (member) => {
    if (!member || !selectedFamily) return;
    
    try {
      // Add back to family memberIDs
      const updatedMemberIDs = [...selectedFamily.memberIDs, member.id];
      await updateDoc(doc(db, 'families', selectedFamily.id), {
        memberIDs: updatedMemberIDs
      });
      
      // Remove from user's removedFamilyIds and add back to familyIds
      const userDoc = await getDoc(doc(db, 'users', member.id));
      const userData = userDoc.data();
      if (userData) {
        const currentFamilyIds = userData.familyIds || [];
        const currentRemovedFamilyIds = userData.removedFamilyIds || [];
        
        // Add back to familyIds and remove from removedFamilyIds
        const updatedFamilyIds = [...currentFamilyIds, selectedFamily.id];
        const updatedRemovedFamilyIds = currentRemovedFamilyIds.filter(id => id !== selectedFamily.id);
        
        await updateDoc(doc(db, 'users', member.id), {
          familyIds: updatedFamilyIds,
          removedFamilyIds: updatedRemovedFamilyIds
        });
      }
      
      // Update local state
      setSelectedFamily({ ...selectedFamily, memberIDs: updatedMemberIDs });
      setMembers(prev => [...prev, member]);
      setRemovedMembers(prev => prev.filter(m => m.id !== member.id));
      
      alert(`${member.displayName || member.email} has been restored to the family.`);
    } catch (error) {
      console.error('Error restoring member:', error);
      alert('Failed to restore member. Please try again.');
    }
  };

  const normalizeTimestamp = (activity) => {
    let ts = activity.createdAt || activity.timestamp || activity.created_at ||
             activity.date || activity.updatedAt || activity.updated_at;
    if (!ts) return { ...activity, createdAt: new Date(0).toISOString() };
    if (ts && typeof ts === 'object' && ts.toDate) ts = ts.toDate().toISOString();
    else if (ts && typeof ts === 'object' && ts.toISOString) ts = ts.toISOString();
    return { ...activity, createdAt: ts };
  };

  const sortNewestFirst = (arr) =>
    [...arr].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  const fetchFamilyActivities = async (familyId) => {
    try {
      // Phase 1: family doc + recordings in parallel (recordings paint first)
      const [familyDoc, recordingsSnapshot] = await Promise.all([
        getDoc(doc(db, 'families', familyId)),
        getDocs(query(collection(db, 'recordings'), where('familyIds', 'array-contains', familyId))),
      ]);

      const familyData = familyDoc.data();
      const recordings = recordingsSnapshot.docs.map(docSnap =>
        normalizeTimestamp({ id: docSnap.id, ...docSnap.data(), type: 'recording', activityType: 'recording_created' })
      );

      // Paint recordings immediately so the feed isn't blank
      setActivities(sortNewestFirst(recordings));
      setCurrentPage(1);

      // Phase 2: all remaining sources in parallel
      const memberIds = familyData ? (familyData.memberIds || familyData.memberIDs || []) : [];

      const fetchMemberNotes = async (memberId) => {
        try {
          const snap = await getDocs(collection(db, `users/${memberId}/notes`));
          return snap.docs
            .filter(d => d.data().book && d.data().content)
            .map(d => {
              const data = d.data();
              const bookName = data.book
                ? data.book.charAt(0).toUpperCase() + data.book.slice(1).replace(/-/g, ' ')
                : '';
              const passage = bookName
                ? `${bookName} ${data.chapter}${data.verse ? ':' + data.verse : ''}`
                : 'Unknown Passage';
              return normalizeTimestamp({
                id: d.id,
                ownerId: memberId,
                ...data,
                type: 'study_note',
                activityType: 'study_note_created',
                passage,
                userName: data.userName || data.userDisplayName || data.userEmail || 'Unknown User',
                userEmail: data.userEmail || 'Unknown Email',
              });
            });
        } catch { return []; }
      };

      const fetchMemberFavorites = async (memberId) => {
        try {
          const snap = await getDocs(collection(db, `users/${memberId}/favoritedVerses`));
          return snap.docs.map(d => {
            const data = d.data();
            return normalizeTimestamp({
              id: d.id,
              ...data,
              type: 'verse_favorite',
              activityType: 'verse_favorite',
              passage: data.passage || 'Unknown Passage',
              userName: data.displayName || 'Unknown User',
              createdAt: data.favoritedAt?.toDate?.()?.toISOString() || data.favoritedAt?.toISOString?.() || null,
            });
          });
        } catch { return []; }
      };

      const fetchMemberBadges = async (memberId) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', memberId));
          const userData = userDoc.data();
          if (!userData?.badges) return [];
          return userData.badges.map((badge, index) => normalizeTimestamp({
            id: `badge_${memberId}_${badge.id || index}`,
            type: 'badge',
            activityType: 'badge_awarded',
            userId: memberId,
            userName: userData.displayName || userData.email,
            userEmail: userData.email,
            badgeName: badge.name,
            badgeDescription: badge.description,
            createdAt: badge.awardedAt || null,
            passage: badge.passage || 'General Achievement',
          }));
        } catch { return []; }
      };

      const [
        notesResults,
        favoritesResults,
        badgesResults,
        listenEventsSnapshot,
      ] = await Promise.all([
        Promise.all(memberIds.map(fetchMemberNotes)),
        Promise.all(memberIds.map(fetchMemberFavorites)),
        Promise.all(memberIds.map(fetchMemberBadges)),
        getDocs(query(collection(db, 'listenEvents'), where('familyIds', 'array-contains', familyId))),
      ]);

      const studyNotes = notesResults.flat();
      const favoritedVerses = favoritesResults.flat();
      const badgeActivities = badgesResults.flat();
      const listenEvents = listenEventsSnapshot.docs.map(docSnap =>
        normalizeTimestamp({ id: docSnap.id, ...docSnap.data(), type: 'listen', activityType: 'chapter_listen' })
      );

      const allActivities = sortNewestFirst([
        ...recordings,
        ...studyNotes,
        ...favoritedVerses,
        ...badgeActivities,
        ...listenEvents,
      ]);

      setActivities(allActivities);
    } catch (error) {
      console.error('Error fetching family activities:', error);
    }
  };

  // ── Note interactions (likes + comments) ─────────────────────────────────
  const noteKey = (activity) => `${activity.ownerId || activity.userId}_${activity.id}`;

  const loadNoteInteractions = async (key) => {
    if (noteInteractions[key]?.loaded) return;
    try {
      const [likesSnap, commentsSnap] = await Promise.all([
        getDocs(query(collection(db, 'noteReactions'), where('noteKey', '==', key))),
        getDocs(query(collection(db, 'noteComments'), where('noteKey', '==', key), orderBy('createdAt', 'asc'))),
      ]);
      setNoteInteractions(prev => ({
        ...prev,
        [key]: {
          likes: likesSnap.docs.map(d => ({ id: d.id, ...d.data() })),
          comments: commentsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
          loaded: true,
        }
      }));
    } catch {
      setNoteInteractions(prev => ({ ...prev, [key]: { likes: [], comments: [], loaded: true } }));
    }
  };

  const handleToggleLike = async (activity) => {
    const key = noteKey(activity);
    await loadNoteInteractions(key);
    const interactions = noteInteractions[key] || { likes: [], comments: [] };
    const existing = interactions.likes.find(l => l.userId === currentUser.uid);
    if (existing) {
      await deleteDoc(doc(db, 'noteReactions', existing.id));
      setNoteInteractions(prev => ({
        ...prev,
        [key]: { ...prev[key], likes: prev[key].likes.filter(l => l.userId !== currentUser.uid) }
      }));
    } else {
      const ref = await addDoc(collection(db, 'noteReactions'), {
        noteKey: key, userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        createdAt: serverTimestamp()
      });
      setNoteInteractions(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          likes: [...(prev[key]?.likes || []), { id: ref.id, userId: currentUser.uid, userName: currentUser.displayName || currentUser.email }]
        }
      }));
    }
  };

  const handleAddComment = async (activity) => {
    const key = noteKey(activity);
    const text = (commentDraft[key] || '').trim();
    if (!text) return;
    const ref = await addDoc(collection(db, 'noteComments'), {
      noteKey: key, userId: currentUser.uid,
      userName: currentUser.displayName || currentUser.email,
      text, familyId: selectedFamilyId,
      createdAt: serverTimestamp()
    });
    setNoteInteractions(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        comments: [...(prev[key]?.comments || []), { id: ref.id, userId: currentUser.uid, userName: currentUser.displayName || currentUser.email, text, createdAt: new Date().toISOString() }]
      }
    }));
    setCommentDraft(prev => ({ ...prev, [key]: '' }));
  };

  const toggleNoteExpand = (key, activity) => {
    const next = expandedNoteId === key ? null : key;
    setExpandedNoteId(next);
    if (next) loadNoteInteractions(key);
  };

  const handleTogglePermission = async (memberId, permission, currentValue) => {
    const newValue = !currentValue;
    try {
      await updateDoc(doc(db, 'families', selectedFamily.id, 'members', memberId), {
        [permission]: newValue,
      });
      setMemberPermissions(prev => ({
        ...prev,
        [memberId]: { ...(prev[memberId] || {}), [permission]: newValue },
      }));
    } catch (err) {
      console.error('Error updating permission:', err);
    }
  };

  const handleToggleActivityLike = async (activity) => {
    const activityId = activity.id;
    const existing = activityLikes[activityId];
    if (existing?.likedByMe) {
      setActivityLikes(prev => ({
        ...prev,
        [activityId]: { count: Math.max(0, prev[activityId].count - 1), likedByMe: false, myLikeId: null }
      }));
      try {
        await deleteDoc(doc(db, 'activityLikes', existing.myLikeId));
      } catch {
        setActivityLikes(prev => ({ ...prev, [activityId]: existing }));
      }
    } else {
      const tempId = `temp_${activityId}`;
      setActivityLikes(prev => ({
        ...prev,
        [activityId]: { count: (prev[activityId]?.count || 0) + 1, likedByMe: true, myLikeId: tempId }
      }));
      try {
        const ref = await addDoc(collection(db, 'activityLikes'), {
          activityId,
          activityType: activity.type,
          activityOwnerId: activity.ownerId || activity.userId,
          activityDescription: activity.passage || activity.badgeName || activity.type,
          likedByUid: currentUser.uid,
          likedByName: currentUser.displayName || currentUser.email,
          createdAt: serverTimestamp(),
        });
        setActivityLikes(prev => ({ ...prev, [activityId]: { ...prev[activityId], myLikeId: ref.id } }));
      } catch {
        setActivityLikes(prev => ({
          ...prev,
          [activityId]: { count: Math.max(0, (prev[activityId]?.count || 1) - 1), likedByMe: false, myLikeId: null }
        }));
      }
    }
  };

  // Filter activities by per-family permissions
  const getPermissionFilteredActivities = () => activities.filter(activity => {
    const uid = activity.ownerId || activity.userId;
    if (!uid) return true;
    const perms = memberPermissions[uid];
    if (!perms) return true;
    if (activity.type === 'recording' && perms.canRecord === false) return false;
    if (activity.type === 'study_note' && perms.canAddNotes === false) return false;
    return true;
  });

  // Pagination functions
  const getCurrentPageActivities = () => {
    const filtered = getPermissionFilteredActivities();
    const startIndex = (currentPage - 1) * activitiesPerPage;
    return filtered.slice(startIndex, startIndex + activitiesPerPage);
  };

  const getTotalPages = () => {
    return Math.ceil(getPermissionFilteredActivities().length / activitiesPerPage);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFamilyPhotoUploadSuccess = async (imageUrl, imageData) => {
    if (!selectedFamily) return;
    
    try {
      // Delete old family photo if it exists
      if (familyPhotoPath) {
        try {
          await deleteFamilyPhoto(familyPhotoPath);
        } catch (error) {
          console.warn('Could not delete old family photo:', error);
        }
      }
      
      // Update family document with new photo
      await updateDoc(doc(db, 'families', selectedFamily.id), {
        familyPhotoUrl: imageUrl,
        familyPhotoPath: imageData.path
      });
      
      // Update local state
      setFamilyPhotoUrl(imageUrl);
      setFamilyPhotoPath(imageData.path);
      setSelectedFamily({ 
        ...selectedFamily, 
        familyPhotoUrl: imageUrl, 
        familyPhotoPath: imageData.path 
      });
      
      // Update families list
      setUserFamilies(prev => 
        prev.map(f => 
          f.id === selectedFamily.id 
            ? { ...f, familyPhotoUrl: imageUrl, familyPhotoPath: imageData.path }
            : f
        )
      );
      
      console.log('Family photo uploaded successfully:', imageUrl);
    } catch (error) {
      console.error('Error updating family photo:', error);
      alert('Photo uploaded but failed to save to family. Please try again.');
    }
  };



  // Show nothing while initializing to prevent flash
  if (initializing) {
    return null;
  }

  if (!currentUser) {
    return (
      <div className="text-center py-16 animate-fade-in">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Please sign in to access family features
        </h2>
        <p className="text-gray-600 dark:text-white/60">
          Create an account or sign in to manage your family's Bible study.
        </p>
      </div>
    );
  }

  if (userFamilies.length === 0) {
    return (
      <div className="text-center py-16 animate-fade-in">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No Families Found</h2>
        <p className="text-gray-600 dark:text-white/60 mb-6">
          You haven't joined any families yet. Create your own family or join an existing one.
        </p>
        <div className="space-x-4">
          <button 
            className="btn-primary"
            onClick={() => navigate('/create-family')}
          >
            Create Family
          </button>
          <button 
            className="btn-secondary"
            onClick={() => navigate('/join-family')}
          >
            Join Family
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-space-cadet via-space-cadet/90 to-cerulean/80 transition-all duration-300 ease-in-out" style={{ minHeight: '100vh', paddingBottom: '80px' }}>
      {selectedFamily && (
        <div className="animate-fade-in">

          {/* Family Header — photo + info side by side */}
          <div className="-mt-4 sm:-mt-6 lg:-mt-8 pt-8 sm:pt-10 pb-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col lg:flex-row gap-6 items-start">

                {/* Photo — natural aspect ratio, no cropping */}
                <div className="w-full lg:w-3/5">
                  {familyPhotoUrl ? (
                    <img
                      src={familyPhotoUrl}
                      alt={selectedFamily.name}
                      className="w-full rounded-[3px] shadow-2xl"
                    />
                  ) : (
                    <div className="w-full aspect-video rounded-[3px] bg-white/10 flex items-center justify-center">
                      <span className="text-white/40 text-sm">No family photo yet</span>
                    </div>
                  )}
                </div>

                {/* Info Panel */}
                <div className="w-full lg:w-2/5 flex flex-col gap-5 lg:pt-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-3 mb-1">
                      <h1 className="text-3xl sm:text-4xl font-bold text-white">{selectedFamily.name}</h1>
                      {userFamilies.length > 1 && (
                        <select
                          value={selectedFamilyId || ''}
                          onChange={(e) => {
                            const familyId = e.target.value;
                            setSelectedFamilyId(familyId);
                            const family = userFamilies.find(f => f.id === familyId);
                            setSelectedFamily(family);
                          }}
                          className="pl-2 pr-7 py-1 bg-white/20 border border-white/30 rounded-[3px] text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/40 transition-all duration-200"
                        >
                          {userFamilies.map(family => (
                            <option key={family.id} value={family.id} className="text-gray-900 dark:text-white bg-white">
                              {family.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    {selectedFamily.bio && (
                      <p className="text-white/70 text-sm leading-relaxed">{selectedFamily.bio}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-8">
                    <div>
                      <div className="text-4xl font-bold text-white">{members.length}</div>
                      <div className="text-xs text-white/50 uppercase tracking-wider mt-0.5">Members</div>
                    </div>
                    <div className="w-px h-12 bg-white/20" />
                    <div>
                      <div className="text-4xl font-bold text-white">{recordings.length}</div>
                      <div className="text-xs text-white/50 uppercase tracking-wider mt-0.5">Passages Recorded</div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Tabs + Content */}
          <div className="pb-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Tab Navigation */}
          <div className="bg-white dark:bg-[#111827] rounded-[3px] p-2 mb-8 shadow-sm border border-cerulean">
            <nav className="flex space-x-1">
              {[
                { id: 'activities', name: 'Activities Feed' },
                { id: 'members', name: 'Family Members' },
                { id: 'completed', name: 'Completed Chapters' },
                { id: 'settings', name: 'Settings' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 rounded-[3px] font-medium text-xs sm:text-sm transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-cerulean text-white shadow-sm'
                      : 'text-gray-600 dark:text-white/60 hover:text-space-cadet dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
                  }`}
                >
                  <span className="hidden sm:inline">{tab.name}</span>
                  <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}

          {activeTab === 'members' && (
            <div className="bg-white dark:bg-[#111827] rounded-[3px] p-4 sm:p-6 shadow-sm border border-cerulean-lightest dark:border-white/10">
              <div className="mb-6">
                <h3 className="text-xl sm:text-2xl font-semibold text-space-cadet dark:text-white">Family Members</h3>
              </div>

              {/* Pending join requests — admin only */}
              {joinRequests.length > 0 && (selectedFamily.createdBy === currentUser.uid || selectedFamily.adminIDs?.includes(currentUser.uid)) && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-base font-semibold text-space-cadet dark:text-white">Pending Requests</h4>
                    <span className="bg-cerulean text-white text-xs font-bold px-2 py-0.5 rounded-full">{joinRequests.length}</span>
                  </div>
                  <div className="space-y-3">
                    {joinRequests.map(request => (
                      <div key={request.id} className="flex items-center gap-3 p-4 bg-cerulean/5 border border-cerulean/20 rounded-[3px]">
                        <div className="w-10 h-10 rounded-full bg-cerulean/20 flex items-center justify-center text-cerulean font-semibold flex-shrink-0">
                          {(request.displayName || request.userEmail || '?')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-space-cadet dark:text-white truncate">{request.displayName || request.userEmail}</p>
                          <p className="text-sm text-gray-500 dark:text-white/40 truncate">{request.userEmail}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleApproveRequest(request)}
                            className="bg-gradient-to-r from-cerulean to-aqua-deep text-white text-sm px-3 py-1.5 rounded-[3px] font-medium hover:opacity-90 transition-all"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleDenyRequest(request)}
                            className="bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-white/60 text-sm px-3 py-1.5 rounded-[3px] font-medium hover:bg-gray-200 dark:hover:bg-white/10 transition-all"
                          >
                            Deny
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-100 dark:border-white/10 mt-6 mb-6" />
                </div>
              )}

              <div className="space-y-3 sm:space-y-4">
                {members.map(member => (
                  <div key={member.id} className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-5 bg-white dark:bg-[#111827] border border-cerulean-lightest dark:border-white/10 rounded-[3px] hover:bg-gray-50 dark:hover:bg-white/5 transition-colors duration-200">
                    <Avatar 
                      user={member} 
                      size="w-12 h-12 sm:w-16 sm:h-16" 
                      className="text-lg sm:text-xl"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-space-cadet dark:text-white text-base sm:text-lg truncate">{member.displayName || member.email}</h4>
                      <p className="text-sm text-gray-600 dark:text-white/60 truncate">{member.email}</p>
                      {member.bio && (
                        <p className="text-sm text-gray-500 dark:text-white/40 mt-1 italic truncate">"{member.bio}"</p>
                      )}
                      <div className="flex items-center space-x-2 mt-2 flex-wrap">
                        {member.id === selectedFamily.createdBy && (
                          <span className="inline-block bg-cerulean text-white text-xs px-2 sm:px-3 py-1 rounded-full font-medium">
                            Creator
                          </span>
                        )}
                        {selectedFamily.adminIDs?.includes(member.id) && member.id !== selectedFamily.createdBy && (
                          <span className="inline-block bg-blue-100 dark:bg-blue-400/15 text-blue-800 dark:text-blue-300 text-xs px-2 sm:px-3 py-1 rounded-full font-medium">
                            Admin
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      {/* View Profile Button - Show for family admins */}
                      {(selectedFamily.createdBy === currentUser.uid || selectedFamily.adminIDs?.includes(currentUser.uid)) && (
                        <button
                          onClick={() => handleViewProfile(member)}
                          className="bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white text-xs sm:text-sm px-3 sm:px-4 py-1 sm:py-2 rounded-[3px] font-medium transition-colors duration-200 flex-shrink-0"
                          title="View member profile"
                        >
                          View Profile
                        </button>
                      )}
                      
                      {/* Admin Management Buttons - Only show for family creator and other admins */}
                      {((selectedFamily.createdBy === currentUser.uid) || 
                        (selectedFamily.adminIDs?.includes(currentUser.uid))) && 
                       member.id !== currentUser.uid && (
                        <div className="flex space-x-2">
                          {selectedFamily.adminIDs?.includes(member.id) ? (
                            <button
                              onClick={() => handleRemoveAdmin(member)}
                              className="bg-red-100 dark:bg-red-400/15 hover:bg-red-200 dark:hover:bg-red-400/25 text-red-700 dark:text-red-300 text-xs sm:text-sm px-3 sm:px-4 py-1 sm:py-2 rounded-[3px] font-medium transition-colors duration-200 flex-shrink-0"
                              title="Remove admin privileges"
                            >
                              Remove Admin
                            </button>
                          ) : (
                            <button
                              onClick={() => handleAddAdmin(member)}
                              className="bg-timberwolf dark:bg-white/10 hover:bg-secondary-300 dark:hover:bg-white/15 text-space-cadet dark:text-white text-xs sm:text-sm px-3 sm:px-4 py-1 sm:py-2 rounded-[3px] font-medium transition-colors duration-200 flex-shrink-0"
                              title="Make this member an admin"
                            >
                              Add Admin
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invite New Members Section - Separate section below members list */}
          {activeTab === 'members' && (
            selectedFamily.createdBy === currentUser.uid ||
            selectedFamily.adminIDs?.includes(currentUser.uid) ||
            memberPermissions[currentUser.uid]?.canInvite === true
          ) && (
            <div className="bg-white dark:bg-[#111827] rounded-[3px] p-4 sm:p-6 shadow-sm border border-cerulean-lightest dark:border-white/10 mb-6 pb-20 mt-6">
              <h4 className="font-semibold text-space-cadet dark:text-white mb-4 text-base sm:text-lg">Invite New Members</h4>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    className="flex-1 px-4 py-3 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/15 rounded-[3px] text-space-cadet dark:text-white focus:ring-2 focus:ring-cerulean focus:border-cerulean transition-all duration-200 text-base sm:text-sm"
                  />
                  <button
                    className="bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white px-4 sm:px-6 py-3 rounded-[3px] font-medium transition-colors duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto text-base sm:text-sm"
                    onClick={handleSendInvite}
                    disabled={sendingInvite || !inviteEmail}
                  >
                    {sendingInvite ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
                <p className="text-sm sm:text-base text-space-cadet dark:text-white opacity-80">
                  Send an invitation to someone to join your family
                </p>
              </div>
            </div>
          )}

          {/* Sent Invitations Log - Separate section */}
          {activeTab === 'members' && invitations.filter(inv => inv.status !== 'accepted').length > 0 && (
            <div className="bg-white dark:bg-[#111827] rounded-[3px] p-4 sm:p-6 shadow-sm border border-cerulean-lightest dark:border-white/10 mb-6 pb-20">
              <h4 className="font-semibold text-space-cadet dark:text-white mb-4 text-base sm:text-lg">Sent Invitations</h4>
              <div className="space-y-3 sm:space-y-4">
                {invitations.filter(inv => inv.status !== 'accepted').map((invitation) => (
                  <div key={invitation.id} className="bg-gray-50 dark:bg-white/5 rounded-[3px] p-3 sm:p-4">
                    {/* Mobile-first layout: Stack content vertically on small screens */}
                    <div className="space-y-3">
                      {/* Email and Status Row */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm sm:text-base font-medium text-space-cadet dark:text-white truncate">
                            {invitation.invitedUserEmail}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-500 dark:text-white/40 mt-1">
                            Sent by {invitation.invitedByName} • {formatFirestoreDate(invitation.createdAt)}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 sm:ml-4">
                          <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-full font-medium whitespace-nowrap ${
                            invitation.status === 'pending' 
                              ? 'bg-yellow-100 dark:bg-yellow-400/15 text-yellow-800 dark:text-yellow-300' 
                              : invitation.status === 'accepted'
                              ? 'bg-green-100 dark:bg-green-400/15 text-green-800 dark:text-green-300'
                              : 'bg-red-100 dark:bg-red-400/15 text-red-800 dark:text-red-300'
                          }`}>
                            {invitation.status}
                          </span>
                        </div>
                      </div>
                      
                      {/* Action Buttons Row - Full width on mobile, inline on desktop */}
                      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 sm:justify-end">
                        {invitation.status === 'pending' && (
                          <button
                            onClick={() => handleResendInvitation(invitation)}
                            className="w-full sm:w-auto bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-2 rounded-[3px] font-medium transition-colors duration-200 shadow-sm"
                          >
                            Resend Invitation
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteInvitation(invitation.id)}
                          className="w-full sm:w-auto bg-red-100 dark:bg-red-400/15 hover:bg-red-200 dark:hover:bg-red-400/25 text-red-700 dark:text-red-300 text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-2 rounded-[3px] font-medium transition-colors duration-200"
                        >
                          Delete Invitation
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-white/40 mt-3 sm:mt-4">
                This log helps you track pending invitations and catch any typos in email addresses. Accepted invitations are automatically archived.
              </p>
            </div>
          )}

          {/* Debug Info - Logged to console instead of UI */}
          {activeTab === 'members' && (() => {
            console.log('🔍 Family Admin Status Debug:', {
              userId: currentUser.uid,
              familyCreator: selectedFamily.createdBy,
              adminIDs: selectedFamily.adminIDs || [],
              isCreator: selectedFamily.createdBy === currentUser.uid,
              isAdmin: selectedFamily.adminIDs?.includes(currentUser.uid),
              canInvite: (selectedFamily.createdBy === currentUser.uid || selectedFamily.adminIDs?.includes(currentUser.uid))
            });
            return null;
          })()}

          {activeTab === 'activities' && (
            <div className="bg-white dark:bg-[#111827] rounded-[3px] p-4 sm:p-6 shadow-sm border border-cerulean-lightest dark:border-white/10">
              <h3 className="text-xl sm:text-2xl font-semibold text-space-cadet dark:text-white mb-4 sm:mb-6">Recent Activities</h3>
              <div className="space-y-3 sm:space-y-4">
                {getCurrentPageActivities().map((activity) => {
                  const memberLookup = members.reduce((acc, m) => { acc[m.id] = m; return acc; }, {});
                  const activityUid = activity.ownerId || activity.userId;
                  const memberProfile = activityUid ? memberLookup[activityUid] : null;
                  const profilePhoto = memberProfile?.photoURL || activity.photoURL || null;
                  return (
                  <div key={activity.id} className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 bg-white dark:bg-[#111827] rounded-[3px] border border-cerulean-lightest dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors duration-200">
                    {profilePhoto ? (
                      <img
                        src={profilePhoto}
                        alt=""
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover shadow-sm flex-shrink-0"
                      />
                    ) : (
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-sm flex-shrink-0 ${
                      activity.type === 'recording' ? 'bg-cerulean/10 text-cerulean' :
                      activity.type === 'study_note' ? 'bg-primary-50 text-cerulean' :
                      activity.type === 'badge' ? 'bg-aquamarine/30 text-space-cadet dark:text-white' :
                      activity.type === 'listen' ? 'bg-aqua-deep/10 text-aqua-deep' :
                      'bg-coral/10 text-coral'
                    }`}>
                      {activity.type === 'recording' && <MicrophoneIcon className="w-5 h-5 sm:w-6 sm:h-6" />}
                      {activity.type === 'study_note' && <PencilSquareIcon className="w-5 h-5 sm:w-6 sm:h-6" />}
                      {activity.type === 'badge' && <TrophyIcon className="w-5 h-5 sm:w-6 sm:h-6" />}
                      {activity.type === 'verse_favorite' && <HeartIcon className="w-5 h-5 sm:w-6 sm:h-6" />}
                      {activity.type === 'listen' && <SpeakerWaveIcon className="w-5 h-5 sm:w-6 sm:h-6" />}
                    </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {activity.type === 'recording' && (
                        <>
                          <p className="text-sm sm:text-base text-space-cadet dark:text-white font-semibold truncate">
                            Recorded: {activity.passage}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-white/60 truncate">
                            {activity.name || activity.userEmail}
                          </p>
                        </>
                      )}
                      {activity.type === 'study_note' && (() => {
                        const key = noteKey(activity);
                        const interactions = noteInteractions[key] || { likes: [], comments: [] };
                        const isExpanded = expandedNoteId === key;
                        return (
                          <>
                            <p className="text-sm sm:text-base text-space-cadet dark:text-white font-semibold">
                              {activity.passage}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-500 dark:text-white/40 mb-1">
                              {activity.userName || activity.userEmail}
                            </p>
                            {activity.content && (
                              <p className="text-sm text-gray-800 dark:text-white/90 leading-relaxed bg-gray-50 dark:bg-white/5 rounded-[3px] px-3 py-2 border border-gray-100 dark:border-white/10">
                                {activity.content}
                              </p>
                            )}
                            {/* Comment button + thread */}
                            {memberPermissions[currentUser.uid]?.canCommentLike !== false && (
                              <>
                                <div className="flex items-center gap-3 mt-2">
                                  <button
                                    onClick={() => toggleNoteExpand(key, activity)}
                                    className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-[3px] text-gray-400 dark:text-white/35 hover:text-cerulean hover:bg-cerulean/10 transition-colors"
                                  >
                                    <ChatBubbleLeftIcon className="w-4 h-4" />
                                    {interactions.comments.length > 0
                                      ? <span>{interactions.comments.length} {interactions.comments.length === 1 ? 'comment' : 'comments'}</span>
                                      : <span>Comment</span>}
                                  </button>
                                </div>
                                {isExpanded && (
                                  <div className="mt-2 space-y-2">
                                    {interactions.comments.map((c, ci) => (
                                      <div key={c.id || ci} className="flex gap-2 text-xs">
                                        <span className="font-semibold text-gray-700 dark:text-white/70 whitespace-nowrap">{c.userName}:</span>
                                        <span className="text-gray-600 dark:text-white/60">{c.text}</span>
                                      </div>
                                    ))}
                                    <div className="flex gap-2 mt-1">
                                      <input
                                        type="text"
                                        value={commentDraft[key] || ''}
                                        onChange={e => setCommentDraft(prev => ({ ...prev, [key]: e.target.value }))}
                                        onKeyDown={e => e.key === 'Enter' && handleAddComment(activity)}
                                        placeholder="Add a comment…"
                                        className="flex-1 text-xs border border-gray-200 dark:border-white/10 rounded-[3px] px-2 py-1 focus:outline-none focus:ring-1 focus:ring-cerulean"
                                      />
                                      <button
                                        onClick={() => handleAddComment(activity)}
                                        className="text-xs px-2 py-1 bg-cerulean text-white rounded-[3px] hover:bg-cerulean/90"
                                      >
                                        Post
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        );
                      })()}
                      {activity.type === 'badge' && (
                        <>
                          <p className="text-sm sm:text-base text-space-cadet dark:text-white font-semibold truncate">
                            Badge Earned: {activity.badgeName}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-white/60 truncate">
                            {activity.userName || activity.userEmail}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-white/40 mt-1 truncate">
                            {activity.badgeDescription}
                          </p>
                        </>
                      )}
                      {activity.type === 'verse_favorite' && (
                        <>
                          <p className="text-sm sm:text-base text-space-cadet dark:text-white font-semibold truncate">
                            Favorited: {activity.passage}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-white/60 truncate">
                            {activity.userName || activity.userEmail}
                          </p>
                          {activity.verseText && (
                            <p className="text-xs text-gray-500 dark:text-white/40 mt-1 truncate">
                              "{activity.verseText.substring(0, 100)}{activity.verseText.length > 100 ? '...' : ''}"
                            </p>
                          )}
                        </>
                      )}
                      {activity.type === 'listen' && (
                        <>
                          <p className="text-sm sm:text-base text-space-cadet dark:text-white font-semibold truncate">
                            Listening: {activity.passage}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-white/60 truncate">
                            {activity.userName || activity.userEmail}
                          </p>
                        </>
                      )}
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-gray-500 dark:text-white/40">{formatFirestoreDate(activity.createdAt)}</p>
                        {(activity.ownerId || activity.userId) !== currentUser.uid &&
                         memberPermissions[currentUser.uid]?.canCommentLike !== false && (
                          <button
                            onClick={() => handleToggleActivityLike(activity)}
                            className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-[3px] transition-colors ${
                              activityLikes[activity.id]?.likedByMe
                                ? 'text-coral'
                                : 'text-gray-400 dark:text-white/35 hover:text-coral hover:bg-red-50 dark:hover:bg-red-400/10'
                            }`}
                            title={activityLikes[activity.id]?.likedByMe ? 'Unlike' : 'Like'}
                          >
                            {activityLikes[activity.id]?.likedByMe
                              ? <HeartSolid className="w-4 h-4" />
                              : <HeartIcon className="w-4 h-4" />}
                            {activityLikes[activity.id]?.count > 0 && (
                              <span>{activityLikes[activity.id].count}</span>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
                {activities.length === 0 && (
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ChartBarIcon className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400 dark:text-white/35" />
                    </div>
                    <p className="text-base sm:text-lg text-gray-500 dark:text-white/40">No activities yet</p>
                    <p className="text-sm sm:text-base text-gray-400 dark:text-white/35 mt-2">Start recording, adding notes, or earning badges to see family activities here</p>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {activities.length > activitiesPerPage && (
                <div className="mt-6 flex items-center justify-between border-t border-cerulean-lightest dark:border-white/10 pt-4">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center rounded-[3px] border border-gray-300 dark:border-white/15 bg-white dark:bg-white/5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-white/70 hover:bg-gray-50 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === getTotalPages()}
                      className="relative ml-3 inline-flex items-center rounded-[3px] border border-gray-300 dark:border-white/15 bg-white dark:bg-white/5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-white/70 hover:bg-gray-50 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700 dark:text-white/70">
                        Showing{' '}
                        <span className="font-medium">{(currentPage - 1) * activitiesPerPage + 1}</span>
                        {' '}to{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * activitiesPerPage, activities.length)}
                        </span>
                        {' '}of{' '}
                        <span className="font-medium">{activities.length}</span>
                        {' '}results
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-[3px] shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center rounded-l-[3px] px-2 py-2 text-gray-400 dark:text-white/35 ring-1 ring-inset ring-gray-300 dark:ring-white/15 hover:bg-gray-50 dark:hover:bg-white/5 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Previous</span>
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                          </svg>
                        </button>
                        
                        {/* Page numbers — windowed around current page */}
                        {(() => {
                          const total = getTotalPages();
                          const pages = [];
                          for (let i = 1; i <= total; i++) {
                            if (i === 1 || i === total || (i >= currentPage - 2 && i <= currentPage + 2)) pages.push(i);
                          }
                          const items = [];
                          let prev = null;
                          for (const page of pages) {
                            if (prev !== null && page - prev > 1) items.push('ellipsis-' + page);
                            items.push(page);
                            prev = page;
                          }
                          return items.map((item) =>
                            typeof item === 'string' ? (
                              <span key={item} className="relative inline-flex items-center px-3 py-2 text-sm text-gray-400 dark:text-white/35 ring-1 ring-inset ring-gray-300 dark:ring-white/15">…</span>
                            ) : (
                              <button
                                key={item}
                                onClick={() => handlePageChange(item)}
                                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                  item === currentPage
                                    ? 'z-10 bg-cerulean text-white'
                                    : 'text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-white/15 hover:bg-gray-50 dark:hover:bg-white/5'
                                }`}
                              >{item}</button>
                            )
                          );
                        })()}
                        ))}
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === getTotalPages()}
                          className="relative inline-flex items-center rounded-r-[3px] px-2 py-2 text-gray-400 dark:text-white/35 ring-1 ring-inset ring-gray-300 dark:ring-white/15 hover:bg-gray-50 dark:hover:bg-white/5 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Next</span>
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'completed' && (
            <div className="bg-white dark:bg-[#111827] rounded-[3px] shadow-sm border border-cerulean-lightest dark:border-white/10 overflow-hidden">
              <CompletedChapters embedded={true} embeddedFamilyId={selectedFamilyId} />
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="bg-white dark:bg-[#111827] rounded-[3px] p-4 sm:p-6 shadow-sm border border-cerulean-lightest dark:border-white/10">
              <h3 className="text-xl sm:text-2xl font-semibold text-space-cadet dark:text-white mb-4 sm:mb-6">Family Settings</h3>
              <div className="space-y-6 sm:space-y-8">

                {/* Family Code — visible only to members with canInvite permission */}
                {memberPermissions[currentUser.uid]?.canInvite && (
                  <div className="p-4 sm:p-6 bg-white dark:bg-[#111827] rounded-[3px] border border-cerulean-lightest dark:border-white/10">
                    <h4 className="font-semibold text-space-cadet dark:text-white mb-1 text-base sm:text-lg">Family Code</h4>
                    <p className="text-sm text-gray-500 dark:text-white/40 mb-4">Share this code with family members so they can find and request to join your family.</p>
                    {selectedFamily.familyCode ? (
                      <div className="flex items-center gap-3">
                        <div className="flex-1 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-[3px] px-4 py-3 text-center font-mono text-2xl font-bold tracking-widest text-space-cadet dark:text-white">
                          {displayFamilyCode(selectedFamily.familyCode)}
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard?.writeText(selectedFamily.familyCode);
                            setCodeCopied(true);
                            setTimeout(() => setCodeCopied(false), 2000);
                          }}
                          className={`flex-shrink-0 flex items-center gap-2 px-4 py-3 rounded-[3px] font-medium text-sm transition-all ${
                            codeCopied
                              ? 'bg-green-100 dark:bg-green-400/15 text-green-700 dark:text-green-300'
                              : 'bg-cerulean/10 hover:bg-cerulean/20 text-cerulean'
                          }`}
                        >
                          {codeCopied ? (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                              </svg>
                              Copied!
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={handleGenerateFamilyCode}
                        disabled={codeGenerating}
                        className="flex items-center gap-2 px-4 py-2.5 bg-cerulean text-white rounded-[3px] font-medium text-sm hover:bg-cerulean/90 transition-all disabled:opacity-50"
                      >
                        {codeGenerating ? (
                          <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /><span>Generating…</span></>
                        ) : (
                          <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg><span>Generate Family Code</span></>
                        )}
                      </button>
                    )}
                  </div>
                )}

                {memberPermissions[currentUser.uid]?.canInvite && (
                  <div className="p-4 sm:p-6 bg-white dark:bg-[#111827] rounded-[3px] border border-cerulean-lightest dark:border-white/10">
                    <h4 className="font-semibold text-space-cadet dark:text-white mb-1 text-base sm:text-lg">Family Photo</h4>
                    <p className="text-sm text-gray-500 dark:text-white/40 mb-4">Upload or change the photo that represents your family.</p>
                    <FamilyImageUpload
                      currentImage={familyPhotoUrl}
                      onUploadSuccess={handleFamilyPhotoUploadSuccess}
                      familyId={selectedFamily.id}
                      size="w-48 h-36"
                    />
                  </div>
                )}

                {(selectedFamily.createdBy === currentUser.uid || selectedFamily.adminIDs?.includes(currentUser.uid)) && (
                  <>
                    <div className="p-4 sm:p-6 bg-white dark:bg-[#111827] rounded-[3px] border border-cerulean-lightest dark:border-white/10">
                      <h4 className="font-semibold text-space-cadet dark:text-white mb-4 text-base sm:text-lg">Family Information</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-space-cadet dark:text-white mb-2">
                            Family Name
                          </label>
                          <input
                            type="text"
                            value={editingFamilyName}
                            onChange={e => setEditingFamilyName(e.target.value)}
                            className="w-full px-4 py-3 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/15 rounded-[3px] text-space-cadet dark:text-white focus:ring-2 focus:ring-cerulean focus:border-cerulean transition-all duration-200"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-space-cadet dark:text-white mb-2">
                            Bio
                          </label>
                          <textarea
                            value={editingFamilyBio}
                            onChange={e => setEditingFamilyBio(e.target.value)}
                            placeholder="A short description of your family…"
                            rows={3}
                            className="w-full px-4 py-3 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/15 rounded-[3px] text-space-cadet dark:text-white focus:ring-2 focus:ring-cerulean focus:border-cerulean transition-all duration-200 resize-none"
                          />
                        </div>
                      </div>
                      <button
                        className="bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-[3px] font-medium transition-colors duration-200 shadow-sm mt-4 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                        onClick={handleSaveFamilyName}
                        disabled={savingFamilyName}
                      >
                        {savingFamilyName ? 'Saving...' : 'Save'}
                      </button>
                    </div>

                    <div className="p-4 sm:p-6 bg-white dark:bg-[#111827] rounded-[3px] border border-cerulean-lightest dark:border-white/10">
                      <h4 className="font-semibold text-space-cadet dark:text-white mb-4 text-base sm:text-lg">Invite New Members</h4>
                      <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                        <input
                          type="email"
                          placeholder="Enter email address"
                          value={inviteEmail}
                          onChange={e => setInviteEmail(e.target.value)}
                          className="flex-1 px-4 py-3 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/15 rounded-[3px] text-space-cadet dark:text-white focus:ring-2 focus:ring-cerulean focus:border-cerulean transition-all duration-200"
                        />
                        <button
                          className="bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white px-4 sm:px-6 py-3 rounded-[3px] font-medium transition-colors duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                          onClick={handleSendInvite}
                          disabled={sendingInvite || !inviteEmail}
                        >
                          {sendingInvite ? 'Sending...' : 'Send Invite'}
                        </button>
                      </div>
                    </div>

                    <div className="p-4 sm:p-6 bg-white dark:bg-[#111827] rounded-[3px] border border-cerulean-lightest dark:border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-space-cadet dark:text-white text-base sm:text-lg">Discoverability</h4>
                          <p className="text-sm text-gray-500 dark:text-white/40 mt-1">
                            When on, your family appears in the "Join a Family" browse list. Members can still join via family code or a direct invite regardless of this setting.
                          </p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={familyListed}
                          disabled={togglingListed}
                          onClick={() => handleToggleListed(!familyListed)}
                          className={`relative ml-4 flex-shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cerulean focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                            familyListed ? 'bg-cerulean' : 'bg-gray-300 dark:bg-white/20'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${familyListed ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {!familyListed && (
                        <p className="mt-3 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                          This family is hidden from the browse list.
                        </p>
                      )}
                    </div>
                  </>
                )}

                <div className="bg-white dark:bg-[#111827] rounded-[3px] border border-cerulean-lightest dark:border-white/10 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setRemovedOpen(v => !v)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 text-left"
                  >
                    <span className="font-semibold text-space-cadet dark:text-white text-base sm:text-lg">
                      Removed Members{removedMembers.length > 0 ? ` (${removedMembers.length})` : ''}
                    </span>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${removedOpen ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {removedOpen && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                      {removedMembers.length > 0 ? (
                        <div className="space-y-3">
                          {removedMembers.map(member => (
                            <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-white/5 rounded-[3px]">
                              <div className="flex items-center space-x-3">
                                <Avatar
                                  user={member}
                                  size="w-10 h-10"
                                  className="text-sm"
                                />
                                <div>
                                  <p className="font-medium text-space-cadet dark:text-white">{member.displayName || member.email}</p>
                                  <p className="text-sm text-gray-600 dark:text-white/60">{member.email}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRestoreMember(member)}
                                className="bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white px-3 py-1 rounded-[3px] text-sm font-medium transition-colors duration-200"
                              >
                                Restore
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 dark:text-white/40 text-sm">No removed members to restore.</p>
                      )}
                    </div>
                  )}
                </div>

              </div>
            </div>
          )}
          </div>
          </div>
        </div>
      )}

      {/* Profile View Drawer */}
      <Transition.Root show={profileModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setProfileModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                    <div className="flex h-full flex-col overflow-y-scroll bg-white dark:bg-[#0d1520] shadow-xl">
                      {/* Header */}
                      <div className="px-4 py-6 sm:px-6 bg-cerulean">
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-lg font-medium text-white">
                            Member Profile
                          </Dialog.Title>
                          <button
                            type="button"
                            className="rounded-[3px] text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={() => setProfileModalOpen(false)}
                          >
                            <span className="sr-only">Close panel</span>
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="relative flex-1 px-4 py-6 sm:px-6">
                        <div className="space-y-6">
                          {/* Avatar and Name */}
                          <div className="flex flex-col items-center text-center">
                            <Avatar 
                              user={memberToView} 
                              size="w-20 h-20" 
                              className="text-2xl mb-4"
                            />
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                              {memberToView?.displayName || memberToView?.email}
                            </h3>
                          </div>

                          {/* Profile Information */}
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm font-medium text-gray-700 dark:text-white/70 mb-1">Email</p>
                              <p className="text-sm text-gray-600 dark:text-white/60 bg-gray-50 dark:bg-white/5 p-3 rounded-[3px]">{memberToView?.email}</p>
                            </div>

                            {memberToView?.bio && (
                              <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-white/70 mb-1">Bio</p>
                                <p className="text-sm text-gray-600 dark:text-white/60 bg-gray-50 dark:bg-white/5 p-3 rounded-[3px] italic">"{memberToView.bio}"</p>
                              </div>
                            )}

                            <div>
                              <p className="text-sm font-medium text-gray-700 dark:text-white/70 mb-2">Role</p>
                              <div className="flex items-center space-x-2">
                                {memberToView?.id === selectedFamily?.createdBy && (
                                  <span className="inline-block bg-cerulean text-white text-sm px-3 py-1 rounded-full font-medium">
                                    Creator
                                  </span>
                                )}
                                {selectedFamily?.adminIDs?.includes(memberToView?.id) && (
                                  <span className="inline-block bg-gray-100 dark:bg-white/5 text-space-cadet dark:text-white text-sm px-3 py-1 rounded-full font-medium">
                                    Admin
                                  </span>
                                )}
                                {memberToView?.id !== selectedFamily?.createdBy && !selectedFamily?.adminIDs?.includes(memberToView?.id) && (
                                  <span className="inline-block bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-white/60 text-sm px-3 py-1 rounded-full font-medium">
                                    Member
                                  </span>
                                )}
                              </div>
                            </div>

                            <div>
                              <p className="text-sm font-medium text-gray-700 dark:text-white/70 mb-1">Joined Family</p>
                              <p className="text-sm text-gray-600 dark:text-white/60 bg-gray-50 dark:bg-white/5 p-3 rounded-[3px]">
                                {memberToView?.createdAt ? formatFirestoreDate(memberToView.createdAt) : 'Unknown'}
                              </p>
                            </div>
                          </div>

                          {/* Per-family permissions — admin/creator only, not for the creator themselves */}
                          {(selectedFamily?.createdBy === currentUser.uid || selectedFamily?.adminIDs?.includes(currentUser.uid)) &&
                           memberToView?.id !== selectedFamily?.createdBy && memberToView?.id !== currentUser.uid && (
                            <div className="pt-4 border-t border-cerulean-lightest dark:border-white/10">
                              <p className="text-sm font-medium text-gray-700 dark:text-white/70 mb-3">Family Permissions</p>
                              <div className="space-y-3">
                                {[
                                  { key: 'canRecord', label: 'Record' },
                                  { key: 'canCommentLike', label: 'Comment & Like' },
                                  { key: 'canAddNotes', label: 'Add Notes' },
                                  { key: 'canInvite', label: 'Invite Others' },
                                ].map(({ key, label }) => {
                                  const value = memberPermissions[memberToView?.id]?.[key] !== false;
                                  return (
                                    <div key={key} className="flex items-center justify-between">
                                      <span className="text-sm text-gray-600 dark:text-white/60">{label}</span>
                                      <button
                                        onClick={() => handleTogglePermission(memberToView.id, key, value)}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${value ? 'bg-cerulean' : 'bg-gray-200 dark:bg-white/10'}`}
                                      >
                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Remove Member Button - Only for admins and not for family creator */}
                          {(selectedFamily?.createdBy === currentUser.uid || selectedFamily?.adminIDs?.includes(currentUser.uid)) &&
                           memberToView?.id !== selectedFamily?.createdBy && (
                            <div className="pt-4 border-t border-cerulean-lightest dark:border-white/10">
                              <button
                                onClick={() => handleRemoveMemberFromDrawer(memberToView)}
                                className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-[3px] font-medium transition-colors duration-200"
                              >
                                Remove from Family
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Add Admin Confirmation Modal */}
      <Transition.Root show={adminModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setAdminModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-[3px] bg-white dark:bg-[#111827] px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-400/15 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.623 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        Add Admin Privileges
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-white/40">
                          Are you sure you want to make <strong>{memberToAdmin?.displayName || memberToAdmin?.email}</strong> an admin?
                        </p>
                        <p className="text-sm text-gray-500 dark:text-white/40 mt-2">
                          Admins can invite new members, manage invitations, and assign admin privileges to other members.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-[3px] bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                      onClick={confirmAddAdmin}
                    >
                      Add Admin
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-[3px] bg-white dark:bg-white/10 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-white/20 hover:bg-gray-50 dark:hover:bg-white/5 sm:mt-0 sm:w-auto"
                      onClick={() => setAdminModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Remove Member Confirmation Modal */}
      <Transition.Root show={removeMemberModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setRemoveMemberModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-[3px] bg-white dark:bg-[#111827] px-4 pb-20 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:pb-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-400/15 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-2.032-1.5-2.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        Remove Family Member
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-white/40">
                          Are you sure you want to remove this member from the family? They will lose access to the family's Bible recordings and notes, but their data will be preserved.
                        </p>
                        {memberToRemove && (
                          <div className="mt-3 p-3 bg-gray-50 dark:bg-white/5 rounded-[3px]">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{memberToRemove.displayName || memberToRemove.email}</p>
                            <p className="text-xs text-gray-500 dark:text-white/40">
                              Email: {memberToRemove.email}
                            </p>
                          </div>
                        )}
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-400/10 rounded-[3px]">
                          <p className="text-sm text-blue-800 dark:text-blue-300">
                            <strong>Note:</strong> This member can be restored later from the Settings tab if needed.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-[3px] bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                      onClick={confirmRemoveMember}
                    >
                      Remove Member
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-[3px] bg-white dark:bg-white/10 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-1 ring-inset ring-gray-300 dark:ring-white/20 hover:bg-gray-50 dark:hover:bg-white/5 sm:mt-0 sm:w-auto"
                      onClick={() => setRemoveMemberModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Delete Confirmation Modal */}
      <Transition.Root show={deleteModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDeleteModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-[3px] bg-white dark:bg-[#111827] px-4 pb-20 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:pb-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-400/15 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-2.032-1.5-2.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        Delete Recording
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-white/40">
                          Are you sure you want to delete this recording? This action cannot be undone.
                        </p>
                        {recordingToDelete && (
                          <div className="mt-3 p-3 bg-gray-50 dark:bg-white/5 rounded-[3px]">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{recordingToDelete.passage}</p>
                            <p className="text-xs text-gray-500 dark:text-white/40">
                              Recorded by: {recordingToDelete.name || recordingToDelete.userEmail}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-[3px] bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                      onClick={confirmDeleteRecording}
                    >
                      Delete Recording
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-[3px] bg-white dark:bg-white/10 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-1 ring-inset ring-gray-300 dark:ring-white/20 hover:bg-gray-50 dark:hover:bg-white/5 sm:mt-0 sm:w-auto"
                      onClick={() => setDeleteModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}

export default FamilyPage;